/**
 * The Denker browser's row, in either lane: where the Denker extension
 * stands in it - set up and closed, it is ready: agents start it when they
 * need it - Set up - the Chrome Web Store setup the user's browsers get, run
 * in the Denker browser - and Open, to sign in to sites there.
 */
import { useCallback, useState } from "react";
import { Button, PocketRow } from "@/components/ui";
import {
  cancelExtensionSetup,
  extensionSetupLabel,
  isExtensionSetupEnd,
  startExtensionSetup,
  useBrowserExtensionSetup,
} from "@/desktop/browser-extension-setup";
import {
  openDenkerBrowser,
  type BrowserExtensionState,
} from "@/desktop/denker-browser";
import { useDenkerBrowserStatus } from "@/desktop/use-denker-browser-status";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { settingsBrowserMessages } from "@/i18n/messages/settings-browser";
import type en from "@/i18n/messages/settings-browser/en";
import {
  ConnectionMessage,
  ConnectionStatus,
  type ConnectionTone,
} from "./browser-connection-chip";

const STATUS = {
  connected: { label: "statusConnected", tone: "connected" },
  consent: { label: "statusWaitingConsent", tone: "attention" },
  outdated: { label: "statusExtensionOutdated", tone: "attention" },
  disabled: { label: "statusExtensionOff", tone: "attention" },
  turned_off: { label: "statusTurnedOff", tone: "attention" },
  installed: { label: "statusExtensionNotConnected", tone: "idle" },
  missing: { label: "statusExtensionNotSetUp", tone: "idle" },
} as const satisfies Record<
  Exclude<BrowserExtensionState, "disconnected">,
  { label: keyof typeof en; tone: ConnectionTone }
>;

const message = (failure: unknown) =>
  failure instanceof Error ? failure.message : String(failure);

export function DenkerBrowserRow() {
  const t = useT(settingsBrowserMessages);
  const tCommon = useT(commonMessages);
  const { status, failed: unread } = useDenkerBrowserStatus();
  const progress = useBrowserExtensionSetup((state) => state.progress);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = useCallback(async () => {
    if (opening) return;
    setOpening(true);
    setError(null);
    try {
      await openDenkerBrowser();
    } catch (failure) {
      setError(message(failure));
    }
    setOpening(false);
  }, [opening]);

  const mine = progress?.denkerBrowser ? progress : null;
  const running = mine && !isExtensionSetupEnd(mine.step) ? mine : null;
  const otherRunning =
    !!progress && !mine && !isExtensionSetupEnd(progress.step);
  const browserId = status?.browserId ?? null;
  const state = status?.extension.state;
  // Set up, and closed: nothing to connect until an agent needs it.
  const ready = !!status?.ready && state === "installed";
  const failed =
    !!mine &&
    isExtensionSetupEnd(mine.step) &&
    !["connected", "cancelled"].includes(mine.step) &&
    state !== "connected";
  // Disconnect is for the user's own browsers alone.
  const extension =
    STATUS[!state || state === "disconnected" ? "installed" : state];
  const { label, tone } = running
    ? {
        label: extensionSetupLabel(running.step, running.browserId, true),
        tone: "idle" as const,
      }
    : !status
      ? {
          label: unread ? t("denkerBrowserCheckFailed") : t("statusChecking"),
          tone: "idle" as const,
        }
      : ready
        ? {
            label: t("denkerBrowserReady"),
            tone: "connected" as const,
          }
        : !browserId
          ? {
              label: t("denkerBrowserNeedsChromium"),
              tone: "idle" as const,
            }
          : { label: t(extension.label), tone: extension.tone };
  // Out of date, the browser updates it: there is nothing to set up.
  const offersSetup =
    !!browserId &&
    !!status?.extension.setup &&
    state !== "connected" &&
    state !== "outdated" &&
    !ready;
  const messages = [
    error ? { text: error, alert: true } : null,
    // A refused start says why above; its "failed" would say it twice.
    failed && mine && !error
      ? {
          text: extensionSetupLabel(mine.step, mine.browserId, true),
          alert: false,
        }
      : null,
  ].filter((line) => line !== null);
  return (
    <PocketRow
      testId="settings-row-denker-browser"
      title={t("denkerBrowserTitle")}
      secondLine={
        <ConnectionStatus
          testId="denker-browser-status"
          tone={tone}
          label={label}
        />
      }
      trailing={
        <>
          {running ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                void cancelExtensionSetup(running.setupId).catch(() => {})
              }
              data-testid="denker-browser-setup-stop"
            >
              {t("stop")}
            </Button>
          ) : offersSetup && browserId ? (
            <Button
              variant="secondary"
              size="sm"
              disabled={otherRunning}
              title={otherRunning ? t("otherSetupRunning") : undefined}
              onClick={() => {
                setError(null);
                // A refusal to start - another setup running, the browser
                // gone - says why; a setup's own end says itself.
                void startExtensionSetup(browserId, true).catch(
                  (failure: unknown) => setError(message(failure)),
                );
              }}
              data-testid="denker-browser-setup"
            >
              {t("actionSetUp")}
            </Button>
          ) : null}
          {/* Hidden only when there is surely no browser to open. */}
          {browserId || !status ? (
            <Button
              variant="secondary"
              size="sm"
              disabled={opening}
              onClick={() => void open()}
              data-testid="denker-browser-open"
            >
              {tCommon("open")}
            </Button>
          ) : null}
        </>
      }
      below={
        messages.length
          ? messages.map((line, at) => (
              <ConnectionMessage key={at} message={line} />
            ))
          : undefined
      }
    />
  );
}
